import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { salesCyclesApi } from '../services/api';
import OpenCyclesTrendsChart from '../components/OpenCyclesTrendsChart';
import { FilterBar } from '../components/filters';

// Parallel data fetching hook for better performance
const useOpenCyclesTrendsPageData = () => {
  // Check if user is authenticated
  const isAuthenticated = !!localStorage.getItem('salesai-api-key');

  // Pre-fetch filter data in parallel - these are cached longer since they change less frequently
  const { data: teamsData } = useQuery(
    'teams',
    salesCyclesApi.getTeams,
    {
      staleTime: 15 * 60 * 1000, // 15 minutes (teams change rarely)
      cacheTime: 30 * 60 * 1000, // 30 minutes
      refetchOnWindowFocus: false,
      enabled: isAuthenticated
    }
  );

  const { data: salesRepsData } = useQuery(
    'salesReps',
    () => salesCyclesApi.getSalesReps(),
    {
      staleTime: 10 * 60 * 1000, // 10 minutes
      cacheTime: 20 * 60 * 1000, // 20 minutes
      refetchOnWindowFocus: false,
      enabled: isAuthenticated
    }
  );

  return {
    teamsData: teamsData || [],
    salesRepsData: salesRepsData || [],
    isAuthenticated
  };
};

const OpenCyclesTrends: React.FC = () => {
  // Use parallel data fetching for better performance
  const { teamsData, salesRepsData } = useOpenCyclesTrendsPageData();

  const [selectedCycleTypes, setSelectedCycleTypes] = useState<string[]>(() => {
    // Use stored value if available, otherwise default to ["NEWBUSINESS"]
    const saved = localStorage.getItem('open-cycles-cycle-type-filter');
    if (!saved) return ['NEWBUSINESS'];

    try {
      const parsed = JSON.parse(saved);
      return Array.isArray(parsed) ? parsed : ['NEWBUSINESS'];
    } catch {
      // If parsing fails, it's likely the old single-string format
      return saved ? [saved] : ['NEWBUSINESS'];
    }
  });

  const [selectedAccountValues, setSelectedAccountValues] = useState<string[]>(() => {
    // Initialize from localStorage if available
    const saved = localStorage.getItem('open-cycles-account-value-filter');
    if (!saved) return [];

    try {
      // Try to parse as JSON array
      const parsed = JSON.parse(saved);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      // If parsing fails, clear old format
      localStorage.removeItem('open-cycles-account-value-filter');
      return [];
    }
  });

  const [selectedSalesRep, setSelectedSalesRep] = useState<string | null>(() => {
    // Initialize from localStorage if available
    const saved = localStorage.getItem('open-cycles-sales-rep-filter');
    return saved || null;
  });

  const [selectedTeam, setSelectedTeam] = useState<string | null>(() => {
    // Initialize from localStorage if available
    const saved = localStorage.getItem('open-cycles-team-filter');
    return saved || null;
  });

  const [selectedMeetingDispositions, setSelectedMeetingDispositions] = useState<string[]>(() => {
    // Initialize from localStorage if available
    const saved = localStorage.getItem('open-cycles-meeting-disposition-filter');
    if (!saved) {
      // Default: all dispositions except BAD_FIT and TRASH, including NOT_ANALYZED (Conflict removed)
      return ['TRANSACTIONAL', 'ENTERPRISE', 'NURTURE', 'NOT_ANALYZED'];
    }

    try {
      const parsed = JSON.parse(saved);
      // Filter out CONFLICT from any saved values (legacy cleanup)
      const filtered = Array.isArray(parsed) ? parsed.filter((v: string) => v !== 'CONFLICT') : ['TRANSACTIONAL', 'ENTERPRISE', 'NURTURE', 'NOT_ANALYZED'];
      return filtered.length > 0 ? filtered : ['TRANSACTIONAL', 'ENTERPRISE', 'NURTURE', 'NOT_ANALYZED'];
    } catch {
      return ['TRANSACTIONAL', 'ENTERPRISE', 'NURTURE', 'NOT_ANALYZED'];
    }
  });

  const handleCycleTypesChange = (cycleTypes: string[]) => {
    setSelectedCycleTypes(cycleTypes);
    // Save to localStorage
    if (cycleTypes.length > 0) {
      localStorage.setItem('open-cycles-cycle-type-filter', JSON.stringify(cycleTypes));
    } else {
      localStorage.removeItem('open-cycles-cycle-type-filter');
    }
  };

  const handleAccountValuesChange = (accountValues: string[]) => {
    setSelectedAccountValues(accountValues);
    // Save to localStorage
    if (accountValues.length > 0) {
      localStorage.setItem('open-cycles-account-value-filter', JSON.stringify(accountValues));
    } else {
      localStorage.removeItem('open-cycles-account-value-filter');
    }
  };

  const handleSalesRepChange = (salesRep: string | null) => {
    setSelectedSalesRep(salesRep);
    // Save to localStorage
    if (salesRep) {
      localStorage.setItem('open-cycles-sales-rep-filter', salesRep);
    } else {
      localStorage.removeItem('open-cycles-sales-rep-filter');
    }
  };

  const handleTeamChange = (team: string | null) => {
    setSelectedTeam(team);
    // Clear the selected sales rep when team changes
    setSelectedSalesRep(null);

    // Save to localStorage
    if (team) {
      localStorage.setItem('open-cycles-team-filter', team);
    } else {
      localStorage.removeItem('open-cycles-team-filter');
    }

    // Clear sales rep from localStorage
    localStorage.removeItem('open-cycles-sales-rep-filter');
  };

  const handleMeetingDispositionsChange = (dispositions: string[]) => {
    setSelectedMeetingDispositions(dispositions);
    // Save to localStorage
    if (dispositions.length > 0) {
      localStorage.setItem('open-cycles-meeting-disposition-filter', JSON.stringify(dispositions));
    } else {
      localStorage.removeItem('open-cycles-meeting-disposition-filter');
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col space-y-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 font-plus-jakarta">Open Sales Cycles</h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm font-inter mt-1">
            Track open sales cycles activity and qualification trends over time
          </p>
        </div>

        {/* Filters */}
        <FilterBar
          selectedCycleTypes={selectedCycleTypes}
          selectedAccountValues={selectedAccountValues}
          selectedMeetingDispositions={selectedMeetingDispositions}
          selectedTeam={selectedTeam}
          selectedSalesRep={selectedSalesRep}
          onCycleTypesChange={handleCycleTypesChange}
          onAccountValuesChange={handleAccountValuesChange}
          onMeetingDispositionsChange={handleMeetingDispositionsChange}
          onTeamChange={handleTeamChange}
          onSalesRepChange={handleSalesRepChange}
          collapsible={true}
          defaultCollapsed={true}
        />
      </div>

      {/* Trends Chart */}
      <OpenCyclesTrendsChart
        selectedCycleTypes={selectedCycleTypes}
        selectedAccountValues={selectedAccountValues}
        selectedMeetingDispositions={selectedMeetingDispositions}
        selectedSalesRep={selectedSalesRep}
        selectedTeam={selectedTeam}
      />
    </div>
  );
};

export default OpenCyclesTrends;
